import { useEffect, useRef, useState } from 'react'
import { useRouterState } from '@tanstack/react-router'
import { useTranslation } from '@lb/i18n'

/**
 * Announces route changes, and moves focus to the top of the new page.
 *
 * ── The gap this closes ───────────────────────────────────────────────────
 * A full page load tells a screen reader the document changed: it reads the
 * new title and focus resets to the top. A client-side navigation does none of
 * that. Without this, someone using a screen reader activates a nav link and
 * hears **nothing at all** — the DOM swaps underneath them and their focus is
 * still on the link they just left, in a sidebar that now describes a different
 * page. It is the single most common accessibility failure in an SPA, and it is
 * invisible to everyone not using assistive technology.
 *
 * Two mechanisms, because they solve different halves:
 *
 *  1. **`aria-live="polite"`** announces the new page's name. Polite, not
 *     assertive — a navigation the member asked for should not interrupt what
 *     is currently being read.
 *  2. **Focus moves to `<main>`.** Announcing alone leaves focus stranded, so
 *     the next Tab continues from the old page's position. `<main>` carries
 *     `tabIndex={-1}` so it can receive focus programmatically without ever
 *     joining the tab order.
 *
 * The document title moves with it, which is what a browser's own history and
 * tab list read.
 *
 * Skipped on first render: the initial load already announced itself, and
 * stealing focus from a fresh page is its own bug.
 */
export function RouteAnnouncer() {
  const { t } = useTranslation()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const [message, setMessage] = useState('')
  const first = useRef(true)

  useEffect(() => {
    if (first.current) {
      first.current = false
      return
    }

    /*
     * Focus and scroll move immediately — those are about the member's
     * position and must not wait on data.
     */
    document.getElementById('main')?.focus({ preventScroll: true })
    window.scrollTo({ top: 0 })

    /*
     * The NAME has to wait, and this is the bug the first version had.
     *
     * Reading `main h1` synchronously announced the page you just LEFT: the
     * pathname changes before the new route's DOM is committed. And every
     * screen here opens on a skeleton while its query is in flight, so even
     * one frame later there is often no `h1` yet — the heading appears when
     * the data does.
     *
     * So this polls briefly for a heading and announces once it has one,
     * giving up after a second rather than waiting forever on a screen whose
     * request failed. `aria-live="polite"` is what makes the delay acceptable:
     * a late announcement is queued behind whatever is being read, which is
     * exactly the behaviour wanted for a page the member navigated to on
     * purpose.
     */
    let cancelled = false
    const started = performance.now()

    const announce = () => {
      if (cancelled) return

      const name = document.querySelector('main h1')?.textContent?.trim()
      if (!name) {
        if (performance.now() - started < 1000) {
          window.setTimeout(announce, 80)
          return
        }
        // No heading arrived — name the page by its route rather than lying
        // about it or announcing nothing at all.
        document.title = t('brand.name')
        setMessage(t('nav.routeChanged', { page: pathname }))
        return
      }

      document.title = `${name} · ${t('brand.name')}`
      setMessage(t('nav.routeChanged', { page: name }))
    }

    /*
     * `setTimeout`, deliberately NOT `requestAnimationFrame`.
     *
     * rAF is frozen in a hidden tab, so the announcement never fired for a
     * page opened in the background — which is precisely what cmd-clicking a
     * nav link now does, since those became real links. Announcing is about
     * the document, not about painting, so it must not be gated on the
     * compositor running.
     *
     * Zero delay: just late enough to be after the route's commit.
     */
    const timer = window.setTimeout(announce, 0)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [pathname, t])

  /*
   * ── The title tracks the HEADING, not only the route ────────────────────
   * A page can change without the URL changing. The community gate is the case
   * that surfaced it: accept the House Rules and the screen becomes the room
   * list, on the same route — so the effect above never re-ran and the tab went
   * on reading "Before you come in" over a list of rooms. A screen reader
   * checking the document title heard the wrong page, and so did anyone
   * scanning their tabs.
   *
   * A `MutationObserver` on `<main>` is the general fix rather than a patch on
   * one screen: any in-route swap — a gate passing, a query failing into an
   * error heading, a step advancing — moves the title with it.
   *
   * **Title only, deliberately not the announcement.** `aria-live` is for
   * NAVIGATION, and re-announcing on every heading mutation would talk over
   * somebody the moment a filter changed a count in an `h1`. The effect above
   * stays the sole announcer.
   */
  useEffect(() => {
    const main = document.getElementById('main')
    if (!main) return

    let last = ''
    const sync = () => {
      const name = main.querySelector('h1')?.textContent?.trim()
      if (!name || name === last) return
      last = name
      document.title = `${name} · ${t('brand.name')}`
    }

    sync()
    const observer = new MutationObserver(sync)
    observer.observe(main, { childList: true, subtree: true, characterData: true })
    return () => observer.disconnect()
  }, [t])

  return (
    <p aria-live="polite" aria-atomic="true" className="sr-only">
      {message}
    </p>
  )
}
